// crewbit-admin/src/components/schedule/WeekCanvas.tsx
//
// Schedule Module Phase C C2 — Week-view top-level orchestrator (admin).
// Composes <WeekHeader> + <ConflictBanner> slot + <WeekGrid> with HTML5
// cross-column drag-drop for date-change reschedule.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3.3 + §3.8.

import { useMemo } from 'react';
import { admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import type { ConflictDetail } from '../../lib/errorMap';
import { ConflictBanner } from './ConflictBanner';
import { WeekGrid } from './WeekGrid';
import { WeekHeader } from './WeekHeader';

export interface WeekCanvasProps {
  /** Anchor date inside the week to render (any day). */
  date: string;
  entries: ScheduleEntry[];
  businessOpen?: string;
  businessClose?: string;
  onEntryTap: (entryId: string) => void;
  /** Date-change drop handler — caller computes Δdays + dispatches RPC. */
  onColumnDrop?: (date: string, droppedEntryId: string) => void;
  onEmptySlotCreate?: (date: string) => void;
  industryAccent?: string;
  conflicts?: ConflictDetail[] | null;
  conflictRpcName?: string;
  onConflictOverride?: () => Promise<void>;
  onConflictDismiss?: () => void;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
  onToday?: () => void;
}

function startOfWeekSunday(iso: string): Date {
  const d = new Date(`${iso}T12:00:00`);
  const dow = d.getDay();
  d.setDate(d.getDate() - dow);
  return d;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function entryIsoDate(entry: ScheduleEntry): string {
  return entry.start_at.slice(0, 10);
}

export function WeekCanvas({
  date,
  entries,
  businessOpen,
  businessClose,
  onEntryTap,
  onColumnDrop,
  onEmptySlotCreate,
  industryAccent,
  conflicts,
  conflictRpcName,
  onConflictOverride,
  onConflictDismiss,
  onPrevWeek,
  onNextWeek,
  onToday,
}: WeekCanvasProps) {
  const weekStart = useMemo(() => startOfWeekSunday(date), [date]);
  const days = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      out.push(toIso(d));
    }
    return out;
  }, [weekStart]);

  const entriesByDate = useMemo(() => {
    const grouped: Record<string, ScheduleEntry[]> = {};
    for (const day of days) grouped[day] = [];
    for (const e of entries) {
      const k = entryIsoDate(e);
      if (grouped[k]) grouped[k].push(e);
    }
    return grouped;
  }, [entries, days]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      flex: 1, height: '100%',
      backgroundColor: admin.bg,
    }}>
      <WeekHeader
        weekStart={weekStart}
        businessOpen={businessOpen}
        businessClose={businessClose}
        onPrev={onPrevWeek}
        onNext={onNextWeek}
        onToday={onToday}
      />

      {conflicts && conflicts.length > 0 && onConflictOverride && onConflictDismiss && conflictRpcName ? (
        <ConflictBanner
          conflicts={conflicts}
          pendingMutationContext={{
            rpcName: conflictRpcName,
            onOverride: onConflictOverride,
          }}
          onDismiss={onConflictDismiss}
        />
      ) : null}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <WeekGrid
          days={days}
          entriesByDate={entriesByDate}
          onEntryTap={onEntryTap}
          onColumnDrop={onColumnDrop}
          onEmptySlotCreate={onEmptySlotCreate}
          industryAccent={industryAccent}
        />
      </div>
    </div>
  );
}
